import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { SecurityDTO } from '../config/security';
import { FeatureRestriction } from './feature-restriction';

/**
 * Raised when ZDHub answered normally but holds no Audit Trail provisioning
 * for the signed-in organisation.
 *
 * This is deliberately distinct from a transport failure. ZDHub reports
 * "not found" with HTTP 200 and a body rather than a 4xx, so without an
 * explicit type the two are indistinguishable at the call site and both end
 * up rendered as "something went wrong". They need different messages: one is
 * "try again later", the other is "your account is not linked to an install".
 */
export class OrgNotProvisionedError extends Error {
  constructor(public readonly orgId: string) {
    super(`No Audit Trail configuration found for organisation ${orgId}`);
    this.name = 'OrgNotProvisionedError';
    // Required so `instanceof` survives the ES5 downlevel of Error subclasses.
    Object.setPrototypeOf(this, OrgNotProvisionedError.prototype);
  }
}

/**
 * True only when an `api/endpoint/{orgId}` response describes a usable
 * Process Core endpoint.
 *
 * ZDHub returns HTTP 200 for a missing org, with a
 * `{"success":false,"message":"Endpoint for orgId:N not found"}` envelope.
 * That parses as a perfectly good object, so any truthiness check on the
 * response treats a missing tenant as a healthy one.
 */
export function isProvisionedEndpoint(data: unknown): boolean {
  if (!data || typeof data !== 'object') {
    return false;
  }
  const endpoint = (data as { endpoint?: unknown }).endpoint;
  return typeof endpoint === 'string' && endpoint.trim().length > 0;
}

@Injectable({
  providedIn: 'root'
})
export class HubService {

  hubURL: string = environment.hubURL;

  private _http = inject(HttpClient);

  getOrgStackURL(): Observable<any> {

    // return of('https://2dv6f798li.execute-api.ca-central-1.amazonaws.com/Prod/');

    // return of('https://localhost:5001/');

    const orgStackURL = sessionStorage.getItem('orgStackURL');
    const orgStackAPIKey = sessionStorage.getItem('orgStackAPIKey');
    const userURL = sessionStorage.getItem('userURL');

    // Treat the literal string "undefined" as missing. setItem(key, undefined)
    // stringifies to "undefined", and prior code paths could write that on a
    // bad response — once cached, it persisted across the whole session and
    // produced URLs like "undefinedapi/Tracker/...". Fall through to a fresh
    // fetch instead.
    if (orgStackURL && orgStackURL !== 'undefined') {
      return of({ endpoint: orgStackURL, apikey: orgStackAPIKey, userURL: userURL });
    }

    const orgId = sessionStorage.getItem('orgId');
    const url: string = this.hubURL + 'api/endpoint/' + orgId;

    return this._http.get<any>(url, { headers: this.getHeaders() })
      .pipe(
        tap(data => {
          if (data && data !== 'error' && data.endpoint) {
            sessionStorage.setItem('orgStackURL', data.endpoint);
            sessionStorage.setItem('orgStackAPIKey', data.apikey);
            sessionStorage.setItem('userURL', data.userEndpoint);

            // ZDHub returns legacyOrgId on rows whose primary key changed during
            // the auth.zeidman.info -> ZeidZone migration. Process Core requests
            // must use this legacy id (see shared/effective-org-id.ts). For
            // unmigrated tenants the field is absent and we clear any stale
            // value from a previous session.
            if (data.legacyOrgId !== undefined && data.legacyOrgId !== null) {
              sessionStorage.setItem('legacyOrgId', String(data.legacyOrgId));
            } else {
              sessionStorage.removeItem('legacyOrgId');
            }
          }
          return data;
        }),
        catchError(this.handleError)
      );

  }

  /////////////////////////////////////

  getOrgDetails(): Observable<any> {
    const orgId = sessionStorage.getItem('orgId') ?? '';
    const url: string = this.hubURL + 'api/nxtaccess/details/' + orgId;

    // ZDHub's Details endpoint answers HTTP 200 with the bare word `error`
    // when it holds no OrgToken for the org. Requesting this as JSON makes
    // Angular fail to parse the body and emit an opaque parse error that is
    // indistinguishable from ZDHub being down — which is exactly how an
    // unmigrated tenant ended up seeing a generic failure at login. Read the
    // body as text and classify it here instead.
    return this._http.get(url, { headers: this.getHeaders(), responseType: 'text' })
      .pipe(
        map(body => {
          const trimmed = (body ?? '').trim();
          let parsed: unknown;
          try {
            parsed = JSON.parse(trimmed);
          } catch {
            // Not JSON at all — the `error` sentinel, or an empty body.
            parsed = trimmed;
          }
          // Anything that is not an object is a "no such org" answer, whether
          // it arrived as `error`, `"error"` or an empty body.
          if (!parsed || typeof parsed !== 'object') {
            throw new OrgNotProvisionedError(orgId);
          }
          return parsed;
        }),
        catchError(err => {
          if (err instanceof OrgNotProvisionedError) {
            return throwError(() => err);
          }
          return this.handleError(err);
        })
      );
  }

  /////////////////////////////////////

  getOrgCloudLocation(): Observable<string> {
    return this.getOrgStackURL().pipe(map(urlDetails => {

      let regionCode;
      const components = urlDetails.endpoint.split('.');

      if (components.length >= 4) {
        const region = components[2];
        switch (region) {
          case "us-east-1":
            regionCode = "US East (N. Virginia)";
            break;
          case "us-east-2":
            regionCode = "US East (Ohio)";
            break;
          case "us-west-2":
            regionCode = "US West (Oregon)";
            break;
          case "ap-southeast-2":
            regionCode = "Asia Pacific (Sydney)";
            break;
          case "ca-central-1":
            regionCode = "Canada (Central)";
            break;
          case "eu-central-1":
            regionCode = "Europe (Frankfurt)";
            break;
          case "eu-west-1":
            regionCode = "Europe (Ireland)";
            break;
          case "eu-west-2":
            regionCode = "Europe (London)";
            break;
          default:
            regionCode = "Unknown";
        }

        return regionCode + " - " + region;
      }
      else if (components.length >= 3) {
        // This is for Azure urls. We return the resource group name
        const start = components[0].split(('/'));
        if (start.length >= 3) {
          const functionApp = start[2];
          return "rg" + functionApp.substring(2);
        }
      }

      return "Unknown";

    }));


  }


  /////////////////////////////////////

  getAllOrgSecurity(): Observable<SecurityDTO[]> {
    const orgId = sessionStorage.getItem('orgId');
    const url: string = this.hubURL + 'api/ATTileAuth/security/' + orgId;

    return this._http.get<SecurityDTO[]>(url, { headers: this.getHeaders() })
      .pipe(catchError(this.handleError));

  }

  /////////////////////////////////////

  updateUserTileSecurity(id: string, act: string, status: boolean): Observable<{ IsError: boolean, ErrorMessage: string }> {
    const url: string = this.hubURL + 'api/ATTileAuth/security/' + act;

    const sec = { Id: id, Status: status };

    return this._http.patch<{ IsError: boolean, ErrorMessage: string }>(url, sec, { headers: this.getHeaders() })
      .pipe(catchError(this.handleError));

  }

  /////////////////////////////////////

  updateUserSecurity(id: string, act: string, status: boolean): Observable<{ IsError: boolean, ErrorMessage: string }> {
    const url: string = this.hubURL + 'api/ATSecurity/security/' + act;

    const sec = { Id: id, Status: status };

    return this._http.patch<{ IsError: boolean, ErrorMessage: string }>(url, sec, { headers: this.getHeaders() })
      .pipe(catchError(this.handleError));

  }

  /////////////////////////////////////

  getConfigVersion(): Observable<string> {
    const orgId = sessionStorage.getItem('orgId');
    const url: string = this.hubURL + 'api/configVersion/version/' + orgId;

    return this._http.get<string>(url, { headers: this.getHeaders() })
      .pipe(catchError(this.handleError));
  }

  /////////////////////////////////////

  isAuthenticationStale(): Observable<boolean> {
    const orgId = sessionStorage.getItem('orgId');
    const url: string = this.hubURL + 'api/nxtaccess/isstale/' + orgId;

    return this._http.get<boolean>(url, { headers: this.getHeaders() })
      .pipe(catchError(this.handleError));
  }

  /////////////////////////////////////

  getAuthenticationURL(): Observable<string> {
    const orgId = sessionStorage.getItem('orgId');
    const url: string = this.hubURL + 'api/nxtaccess/oauth/audittrail/url?orgId=' + orgId;

    return this._http.get(url, { headers: this.getHeaders(), responseType: 'text' })
      .pipe(catchError(this.handleError));
  }

  /////////////////////////////////////

  getFeatureRestrictions(): Observable<FeatureRestriction[]> {
    const orgId = sessionStorage.getItem('orgId');
    const url: string = this.hubURL + 'api/featurerestriction/' + orgId;

    return this._http.get<FeatureRestriction[]>(url, { headers: this.getHeaders() })
      .pipe(catchError(this.handleError));
  }

  /////////////////////////////////////

  private getHeaders(): HttpHeaders {

    let headers = new HttpHeaders();

    headers = headers.set('x-api-key', environment.hubAPIKey);
    return headers;
  }

  /////////////////////////////////////

  private handleError(err: HttpErrorResponse) {
    console.log(err.message);
    return throwError(err.message);
  }



}
